
import { mapGetters, mapActions } from 'vuex'
import ProductCardMixin from '@made-people/centra-storyblok-nuxt-shared/lib/mixins/ProductCardMixin'
import ViewportMixin from '~/mixins/viewport'
import TrashIcon from '~/assets/icons/trash.svg'
import WishlistIcon from '~/components/wishlist/WishlistIcon'
import CartIcon from '~/assets/icons/cart.svg'

export default {
  name: 'ProductCardAlgolia',
  components: {
    TrashIcon,
    WishlistIcon,
    CartIcon,
  },
  mixins: [
    ProductCardMixin,
    ViewportMixin
  ],
  props: {
    gtmListName: {
      type: String,
      default () {
        return this.$route.path.replace(/(^\/[^/]+\/?)|(\/$)/g, '')
      }
    },
    position: {
      type: Number,
      required: true
    },
    showWishlistIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    showTrashIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Image display sizes for optimized images
     * Can be sent from a parent, ie the grid component
     */
    mobileDisplayWidthOfViewportWidth: {
      type: Number,
      default: 100
    },
    desktopDisplayWidthOfViewportWidth: {
      type: Number,
      default: 100
    },
    isInSearch: {
      type: Boolean,
      default: false,
      required: false
    },
    contextSearch: {
      type: Boolean,
      required: false,
      default: false
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    queryID: {
      type: String,
      required: false,
      default: () => '',
    },
  },
  data () {
    return {
      loaded: false,
      isTabletOrDesktop: false,
      isMobile: true,
      discountBadges: []
    }
  },
  computed: {
    ...mapGetters({
      pricelist: 'frontend/pricelist',
      getProductById: 'centra-product/getProductById',
      generalSettings: 'storyblok/settings',
      currentLanguageCode: 'frontend/currentLanguageCode',
    }),
    localizedBadges () {
      return this.product.badge || {}
    },
    productUrl () {
      if (this.product.isBundle) {
        return this.centraProduct ? this.centraProduct.url : ''
      } else {
        return this.$u(this.product.url)
      }
    },
    imageProxyUrl () { return this.$config.imageProxyUrl },
    isOnesize () {
      return this.product.isOnesize
    },
    isInWishlist () {
      return this.hasProductID(this.product.id)
    },
    name () {
      return this.product.name
    },
    price () {
      return this.product.prices[this.pricelist?.pricelist]?.price || this.product.price
    },
    priceBeforeDiscount () {
      return this.product.prices[this.pricelist?.pricelist]?.priceBeforeDiscount || ''
    },
    isOnSale () {
      return this.product.prices[this.pricelist?.pricelist]?.priceBeforeDiscount !== this.product.prices[this.pricelist?.pricelist]?.price
    },
    currentRouteIsCheckout () {
      return this.$route.name === 'context-checkout'
    },
    sizeOptions () {
      return this.centraProduct?.sizeOptions || []
    },
    isGiftcard () {
      return this.product && this.product.variantName === 'Giftcard'
    }
  },
  mounted () {
    this.discountBadges = JSON.parse(JSON.stringify(this.getDiscountBadges())).map((badge) => {
      return {
        color: badge.color,
        text: badge.text
      }
    })
    this.setViewport()
    window.addEventListener('resize', this.setViewport)
  },
  destroyed () {
    window.removeEventListener('resize', this.setViewport)
  },
  created () {
    if (this.product.isBundle) {
      this.fetchCentraProduct(this.product.sku)
    }
  },
  methods: {
    ...mapActions({
      hideSearch: 'ui/hideSearch',
    }),
    badgeBackgroundColor (badge) {
      return this.generalSettings?.pdpBadgeColors?.[0]?.['color_' + badge.color]?.color ?? '#383838'
    },
    badgeTextColor (badge) {
      return this.generalSettings?.pdpTextColorForBadge?.[0]?.['color_' + badge.color]?.color ?? '#FFFFFF'
    },
    productBadges () {
      const badgesColorAndText = []
      for (const [key, value] of Object.entries(this.localizedBadges)) {
        badgesColorAndText.push({
          color: key,
          text: value
        })
      }
      if (this.discountBadges.length > 0) {
        badgesColorAndText.unshift(...this.discountBadges)
      }
      return badgesColorAndText
    },
    handleOnClick () {
      this.gtm_trackProductClick(this.gtmListName, this.product, this.position)
    },
    inViewportHandler () {
      if (this.position === 0) {
        this.gtm_trackPLPLoaded()
      }
      const list = this.gtmListName || this.$route.path.replace(/(^\/[^/]+\/?)|(\/$)/g, '')
      this.gtm_queueProductImpression(this.product, list, this.position)
    },
    gtm_queueProductImpression (product, list, position) {
      try {
        window.queuedProductImpressions.push({
          parentId: product.sku.substring(0, 4),
          productId: product.sku,
          id: product.sku,
          title: product.name,
          name: product.sku.substring(0, 4),
          price: parseFloat(product.prices[this.pricelist?.pricelist]?.price.replace(/^\D+/g, '')) || parseFloat(product.price),
          category: product.folder,
          list,
          position,
          currency: this.pricelist?.name,
          item_variant: product.variantName
        })
        const flushImpressionQueue = () => {
          this.gtm_trackProductImpression(window.queuedProductImpressions)
          window.queuedProductImpressions = []
        }
        clearTimeout(window.impressionPushTimer)
        if (window.queuedProductImpressions.length >= 20) {
          flushImpressionQueue()
        } else {
          window.impressionPushTimer = setTimeout(flushImpressionQueue, 500)
        }
      } catch (e) {
        console.warn(e)
      }
    },
    imageLoaded () {
      this.loaded = true
    },
    toggleAddToWishlist () {
      if (this.isInWishlist) {
        this.removeFromWishlist()
      } else {
        this.addToWishlist()
      }
    },
    removeFromWishlist () {
      this.$store.dispatch('wishlist/removeProductID', this.product.id)
    },
    addToWishlist () {
      this.$store.dispatch('wishlist/addProductID', this.product.id)
    },
    // here, sku is the 6 digit article number
    sendDataToPLP () {
      this.$emit('sendDataToProductClickTrigger', this.product.objectID, this.position)
    },
    handleOnMouseOver () {
      this.fetchCentraProduct(this.product.sku)
    },
    async fetchCentraProduct (productSku) {
      if (this.centraProduct !== null) { return }
      await this.$store.dispatch('centra-product/lookupProductBySku', productSku)
        .then((product) => {
          this.centraProduct = product
        })
        .catch((error) => {
          console.error('Failed to fetch centra product', error)
          this.centraProduct = {}
        })
    },
    fireAtcClickTrackingEvent () {
      this.gtm_trackQuickAddToCartClick({
        sku: this.product?.sku,
        name: this.product?.name,
        variantName: this.product?.variantName,
        price: this.product?.prices?.[this.pricelist.pricelist]?.priceAsNumber,
        isBundle: this.product?.isBundle,
      })
    },
    handleAtcClick () {
      if (this.sizeOptions.length) {
        this.showSizeSelector()
        this.fireAtcClickTrackingEvent()
      } else {
        this.fetchCentraProduct(this.product.sku).then(() => {
          if (this.sizeOptions.length) {
            this.fireAtcClickTrackingEvent()
            this.showSizeSelector()
          }
        })
      }
    },
    showSizeSelector () {
      if (this.isTabletOrDesktop) {
        this.$store.dispatch(
          'ui/showSizeSelectorDesktop',
          {
            productID: this.product.objectID,
            discontinued: false,
            queryID: this.queryID,
          }
        )
      } else {
        this.$store.dispatch(
          'ui/showSizeSelectorMobile',
          {
            productID: this.product.objectID,
            discontinued: false,
            queryID: this.queryID,
          }
        )
      }
    },
    setViewport () {
      this.isTabletOrDesktop = window.innerWidth >= 768
      this.isMobile = window.innerWidth < 768
    },
    getDiscountBadges () {
      const badges = []
      const priceAsNumber = this.product?.prices?.[this.pricelist?.pricelist]?.priceAsNumber || -1
      const priceBeforeDiscountAsNumber = this.product?.prices?.[this.pricelist?.pricelist]?.priceBeforeDiscountAsNumber || -1
      if (priceAsNumber !== -1 && priceBeforeDiscountAsNumber !== -1) {
        if (priceBeforeDiscountAsNumber > priceAsNumber) {
          const discountPercentage = Math.round((priceBeforeDiscountAsNumber - priceAsNumber) / priceBeforeDiscountAsNumber * 100)
          badges.push({
            text: '-' + discountPercentage.toString() + '%',
            color: 'discount'
          })
        }
      }
      return badges
    },
    closeSearch () {
      if (!this.isInSearch) {
        return
      }

      this.hideSearch()
    },
  }
}
